import React, { useEffect, useState } from 'react';
import { ListTodo, Phone } from 'lucide-react';
import ClientResource from 'App/resources/Client';
import type Client from 'App/resources/Model/Client';
import type Contact from 'App/resources/Model/Contact';
import { openDrawer } from 'Services/Drawer';
import { openEditActivity } from 'App/components/EditActivityModal/openEditActivity';
import { contactCreateAvailable } from 'App/pages/ContactCard/QuickCreate/availability';
import T from 'Components/Helpers/translate';

/**
 * Plan a phone call or create an activity for the profile's contact without leaving the page
 * (WEB-15181). The same forms, and the same gates, as the contact card's Add menu; on a
 * deactivated account that menu hides the call and greys out the activity, so both go here.
 * The account is read like the contact card reads it: `contact.client` carries no `active`.
 */
const ContactCreateActions: React.FC<{ contact: Contact }> = ({ contact }) => {
	const [client, setClient] = useState<Client | null>(null);
	const clientId = contact.client?.id;

	useEffect(() => {
		setClient(null);
		if (!clientId) return;
		let cancelled = false;
		ClientResource.get(clientId)
			.then(({ data }) => {
				if (!cancelled) setClient(data);
			})
			.catch((err: unknown) => {
				console.error('[EmbedAIChat] reading the account failed', { clientId, err });
			});
		return () => {
			cancelled = true;
		};
	}, [clientId]);

	if (!client) return null;
	const available = contactCreateAvailable(client);
	if (available.accountInactive || !(available.todo || available.activity)) return null;

	return (
		<div className="embed-add-actions">
			{available.todo ? (
				<button type="button" className="embed-add-btn" onClick={() => openDrawer('CreateCall', { contact })}>
					<Phone />
					{T('todo.planACall')}
				</button>
			) : null}
			{available.activity ? (
				<button
					type="button"
					className="embed-add-btn"
					onClick={() => openEditActivity({ activity: { contacts: { id: contact.id } } })}
				>
					<ListTodo />
					{T('default.activity')}
				</button>
			) : null}
		</div>
	);
};

export default ContactCreateActions;
